import WbSunnyIcon from "@mui/icons-material/WbSunny";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";

const has = (v) => v !== null && v !== undefined;

// A caregiver needs to know how fresh a reading is — NWS "current conditions" is
// the current-hour forecast, not a live observation, so surface when it was pulled.
const formatObservedAt = (iso) => {
  if (!has(iso)) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * GMA-875 — Weather & Hazards card on the existing client profile (not the
 * Command Center). Shows current conditions from both sources side by side
 * (Apple WeatherKit + NWS) so a caregiver can compare, plus any active
 * flood / storm / heat / air-quality alert for the client's area.
 *
 * Three async states: loading / friendly-error / data. A provider that is
 * unavailable (e.g. Apple key not configured) is shown as "unavailable" rather
 * than hiding the whole card — the other source still renders.
 *
 * Weather fetch is owned by the parent (useClientWeather) so Device location
 * cards share the same snapshot and refresh.
 */

const SEVERITY_COLOR = {
  Extreme: "#b71c1c",
  Severe: "#e65100",
  Moderate: "#f9a825",
  Minor: "#2e7d32",
  Unknown: "#616161",
};

// The Severe (#e65100) and Moderate (#f9a825) backgrounds are too light for
// white text to clear WCAG AA (4.5:1) at 12px, so those chips use dark text.
// Severe needs pure black to clear AA (#000 on #e65100 = 5.54:1; #212121 was
// only 4.25:1); Moderate clears comfortably with #212121 (8.17:1 on #f9a825).
// The remaining (darker) backgrounds keep white. Default is white.
const SEVERITY_TEXT = {
  Severe: "#000000",
  Moderate: "#212121",
};

const providerLabel = { apple: "Apple WeatherKit", nws: "National Weather Service" };

// WCAG AA (4.5:1) text colors for the light Weather card surface (#FFF8E1):
// the pre-existing #777/#888/#999 greys failed AA on that background, so the
// caregiver-facing labels use #4a4a4a (primary) and #616161 (secondary).
const LABEL_COLOR = "#4a4a4a";
const SECONDARY_COLOR = "#616161";

/**
 * Fixed detail-row order for BOTH providers so columns line up for comparison.
 * Missing values render as "—" (not omitted) — otherwise Humidity jumps and
 * Apple's UV vs NWS precip make the lists look "off" even when numbers are fine.
 */
export const WEATHER_DETAIL_ROWS = [
  {
    key: "apparentTempF",
    label: "Feels like",
    format: (d) => (has(d.apparentTempF) ? `${d.apparentTempF}°F` : null),
  },
  {
    key: "humidity",
    label: "Humidity",
    format: (d) => (has(d.humidity) ? `${d.humidity}%` : null),
  },
  {
    key: "wind",
    label: "Wind",
    format: (d) =>
      has(d.windMph)
        ? `${d.windMph} mph${d.windDir ? ` ${d.windDir}` : ""}`
        : null,
  },
  {
    key: "precipChance",
    label: "Chance of precip",
    format: (d) => (has(d.precipChance) ? `${d.precipChance}%` : null),
  },
  {
    key: "dewpointF",
    label: "Dew point",
    format: (d) => (has(d.dewpointF) ? `${d.dewpointF}°F` : null),
  },
  {
    key: "uvIndex",
    label: "UV",
    format: (d) => (has(d.uvIndex) ? String(d.uvIndex) : null),
  },
];

/** Why Apple vs NWS can disagree (Cloudy vs Sunny at the same °F). */
export const WEATHER_SOURCE_COMPARE_HINT =
  "Apple WeatherKit is a near-live observation; NWS here is the current-hour forecast. Sky and wind can differ even when temperatures match.";

// Disabled/elderly safety lens (GMA-875): translate the raw numbers into what a
// caregiver must actually watch for. "Feels like" (apparent temp) drives
// heat/cold risk; precipitation drives fall risk. Returns the single most-severe
// advisory, or null on a mild day so there are no false alarms.
const ADVISORY_STYLE = {
  danger: { backgroundColor: "#b71c1c", color: "#fff" },
  caution: { backgroundColor: "#e65100", color: "#000" },
  info: { backgroundColor: "#e3f2fd", color: "#0d47a1" },
};

export const weatherSafetyAdvisory = (reading) => {
  if (!reading || !reading.available) return null;
  const feels = has(reading.apparentTempF) ? reading.apparentTempF : reading.tempF;
  const cond = (reading.condition || "").toLowerCase();
  const wet =
    /rain|snow|sleet|ice|icy|shower|storm|thunder|wintry|freezing/.test(cond) ||
    (has(reading.precipChance) && reading.precipChance >= 50);
  if (has(feels) && feels >= 103)
    return { level: "danger", text: `Extreme heat (feels like ${feels}°F) — high heat-exhaustion risk. Keep the client cool, hydrated, and out of the sun.` };
  if (has(feels) && feels <= 15)
    return { level: "danger", text: `Dangerous cold (feels like ${feels}°F) — hypothermia and frostbite risk; icy ground is a fall hazard. Keep the client warm and indoors.` };
  if (has(feels) && feels >= 90)
    return { level: "caution", text: `Hot (feels like ${feels}°F) — heat is hard on elderly clients. Encourage fluids and limit time outdoors.` };
  if ((has(feels) && feels <= 32) || (has(reading.tempF) && reading.tempF <= 32))
    return { level: "caution", text: `Freezing — bundle the client up and watch for slick, icy surfaces (fall risk).` };
  if (wet)
    return { level: "info", text: "Wet or icy conditions expected — slip-and-fall risk. Take care on walks and wipe down walkers/wheelchairs." };
  return null;
};

// Each provider renders CELLS into the shared comparison grid (the wrapper is
// display:contents so the per-provider testid survives without breaking grid
// placement). Every field pair shares an explicit grid ROW, so a wrapped
// condition or wind value in one column can never shift the other column's
// rows out of alignment — corresponding readings always sit side by side.
// Row map: 1 name · 2 temp · 3 condition · 4..N detail rows · last observedAt.
function ConditionColumn({ provider, data, column }) {
  const name = providerLabel[provider] || provider;
  const cell = (row, style = {}) => ({ gridColumn: column, gridRow: row, ...style });
  if (!data || !data.available) {
    const reason =
      data?.reason === "not_configured"
        ? "not configured yet"
        : data?.reason === "no_location"
          ? "no location on file"
          : "unavailable";
    return (
      <div style={{ display: "contents" }} data-testid={`weather-col-${provider}`}>
        <div style={cell(1, { fontSize: 12, color: LABEL_COLOR })}>{name}</div>
        <div style={cell(2, { color: SECONDARY_COLOR, fontStyle: "italic" })}>{reason}</div>
      </div>
    );
  }
  // formatObservedAt returns "" for a missing or unparseable timestamp; gate the
  // whole label on the formatted result so an invalid fetchedAt renders nothing
  // instead of a bare "as of ".
  const observedAt = formatObservedAt(data.fetchedAt);
  return (
    <div style={{ display: "contents" }} data-testid={`weather-col-${provider}`}>
      <div style={cell(1, { fontSize: 12, color: LABEL_COLOR })}>{name}</div>
      <div style={cell(2, { fontSize: 26, fontWeight: 600, color: "#212121" })}>
        {has(data.tempF) ? `${data.tempF}°F` : "—"}
      </div>
      <div style={cell(3, { color: "#333" })}>{data.condition || "—"}</div>
      {WEATHER_DETAIL_ROWS.map((row, i) => {
        const value = row.format(data);
        return (
          <div
            key={row.key}
            data-testid={`weather-row-${provider}-${row.key}`}
            style={cell(4 + i, { fontSize: 12, color: SECONDARY_COLOR })}
          >
            {row.label} {value ?? "—"}
          </div>
        );
      })}
      {observedAt && (
        <div
          style={cell(4 + WEATHER_DETAIL_ROWS.length, {
            fontSize: 11,
            color: SECONDARY_COLOR,
            marginTop: 4,
          })}
        >
          as of {observedAt}
        </div>
      )}
    </div>
  );
}

export default function WeatherHazardsSection({
  loading = false,
  error = null,
  data = null,
  onRefresh,
}) {
  return (
    <div
      className="watch-vitals-section"
      style={{
        // GMA-875: this card carries its own light surface so its dark
        // reading text never depends on the page background (which was black
        // when this shipped, bg-page-light since #639). A light amber surface
        // matches the sunny theme.
        backgroundColor: "#FFF8E1",
        color: "#333",
        border: "1px solid #FFE0B2",
        borderLeft: "4px solid #F9A825",
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
        marginBottom: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WbSunnyIcon fontSize="small" />
          <h3 style={{ margin: 0 }}>Weather &amp; Hazards</h3>
        </div>
        <button
          type="button"
          aria-label="Refresh weather"
          onClick={onRefresh}
          disabled={loading || !onRefresh}
          style={{
            border: "none",
            background: "none",
            cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.5 : 1,
          }}
        >
          <RefreshIcon fontSize="small" />
        </button>
      </div>

      {loading && <p style={{ color: "#616161" }}>Loading weather…</p>}
      {!loading && error && <p style={{ color: "#b71c1c" }}>{error}</p>}

      {!loading && !error && data && (
        <>
          {data.location && data.location.hasLocation === false ? (
            // No location: alerts can't be checked either, so we do NOT render an
            // "Active hazard alerts / No active alerts" section here — that would
            // be false reassurance. The message covers both weather and alerts.
            <p style={{ color: "#616161", fontStyle: "italic" }}>
              No usable map coordinates for this client yet — weather and hazard
              alerts need a home address that has been geocoded, or a recent
              location reported by the client&apos;s MyCompass device.
            </p>
          ) : (
            <>
              {(() => {
                // Prefer the source that has a reading (NWS is the reliable one).
                const reading = data.nws?.available ? data.nws : data.apple;
                const advisory = weatherSafetyAdvisory(reading);
                if (!advisory) return null;
                return (
                  <div
                    role="status"
                    style={{
                      ...ADVISORY_STYLE[advisory.level],
                      borderRadius: 6,
                      padding: "8px 12px",
                      marginTop: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      lineHeight: 1.35,
                    }}
                  >
                    <WarningAmberIcon fontSize="small" style={{ flexShrink: 0 }} />
                    <span>{advisory.text}</span>
                  </div>
                );
              })()}
              <div
                data-testid="weather-compare-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(140px, 1fr))",
                  columnGap: 24,
                  marginTop: 8,
                  alignItems: "baseline",
                }}
              >
                <ConditionColumn provider="apple" data={data.apple} column={1} />
                <ConditionColumn provider="nws" data={data.nws} column={2} />
              </div>
              {data.apple?.available && data.nws?.available && (
                <p
                  data-testid="weather-source-compare-hint"
                  style={{
                    fontSize: 11,
                    color: SECONDARY_COLOR,
                    margin: "8px 0 0",
                    fontStyle: "italic",
                    lineHeight: 1.35,
                  }}
                >
                  {WEATHER_SOURCE_COMPARE_HINT}
                </p>
              )}

              <div style={{ marginTop: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                  <WarningAmberIcon fontSize="small" /> Active hazard alerts
                </div>
                {(!data.alerts || data.alerts.length === 0) && (
                  <p style={{ color: "#2e7d32", margin: "6px 0" }}>No active alerts for this client&apos;s area.</p>
                )}
                {data.alerts && data.alerts.length > 0 && (
                  <ul style={{ listStyle: "none", padding: 0, margin: "6px 0" }}>
                    {data.alerts.map((a, i) => (
                      <li
                        key={a.identifier || i}
                        style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "6px 0" }}
                      >
                        <span
                          style={{
                            background: SEVERITY_COLOR[a.severity] || SEVERITY_COLOR.Unknown,
                            color: SEVERITY_TEXT[a.severity] || "#fff",
                            borderRadius: 4,
                            padding: "2px 8px",
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          {a.severity}
                        </span>
                        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                          <span style={{ fontWeight: 600, color: "#212121" }}>
                            {a.event || a.headline || "Alert"}
                          </span>
                          {/* CAP urgency — the caregiver's triage cue. "Immediate"
                              means act now; emphasize it, show the rest plainly. */}
                          {has(a.urgency) && a.urgency !== "Unknown" && (
                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: a.urgency === "Immediate" ? "#b71c1c" : SECONDARY_COLOR,
                              }}
                            >
                              {a.urgency === "Immediate" ? "Respond now" : `Urgency: ${a.urgency}`}
                            </span>
                          )}
                          {has(a.expires) && formatObservedAt(a.expires) && (
                            <span style={{ fontSize: 12, color: SECONDARY_COLOR }}>
                              In effect until {formatObservedAt(a.expires)}
                            </span>
                          )}
                          {/* CAP response instruction — what the caregiver should actually do. */}
                          {has(a.instruction) && (
                            <span style={{ fontSize: 12, color: "#333" }}>{a.instruction}</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
